import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getVariantsByProduct } from '../api';

const SIZE_UNITS = ['S', 'M', 'L', 'XL', 'XXL'];
const API_BASE_URL = process.env.REACT_APP_API_URL;

const normalizeVariantId = (variantId) => {
    if (variantId === null || variantId === undefined) return 'default';
    if (variantId === 'default') return 'default';
    return String(variantId);
};

const makeCartId = (productId, variantId) =>
    `${productId}-${normalizeVariantId(variantId)}`;

const getCartedQty = (productId, variantId) => {
    const cart   = JSON.parse(localStorage.getItem('cart')) || [];
    const cartId = makeCartId(productId, variantId);
    const item   = cart.find(i => i.cartId === cartId);
    return item ? item.quantity : 0;
};

const Stars = ({ rating = 5, size = 14, interactive = false, onRate }) => (
    <span style={{ display: 'inline-flex', gap: 2 }}>
        {[1, 2, 3, 4, 5].map(s => (
            <svg key={s} width={size} height={size} viewBox="0 0 20 20"
                onClick={() => interactive && onRate && onRate(s)}
                style={{ cursor: interactive ? 'pointer' : 'default', transition: 'transform .15s' }}
                onMouseEnter={e => interactive && (e.currentTarget.style.transform = 'scale(1.3)')}
                onMouseLeave={e => interactive && (e.currentTarget.style.transform = 'scale(1)')}>
                <polygon points="10,1 12.9,7 19.5,7.6 14.5,12 16.2,18.5 10,15 3.8,18.5 5.5,12 0.5,7.6 7.1,7"
                    fill={s <= Math.round(rating) ? '#F59E0B' : '#E5E7EB'}
                    stroke={s <= Math.round(rating) ? '#D97706' : '#D1D5DB'}
                    strokeWidth="0.5" />
            </svg>
        ))}
    </span>
);

const Avatar = ({ name = '?' }) => {
    const initials = name.trim().split(' ').map(w => w[0]?.toUpperCase()).slice(0, 2).join('');
    const hue = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
    return (
        <div style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            background: `hsl(${hue},60%,45%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Sora', sans-serif",
            boxShadow: `0 0 0 2px hsl(${hue},60%,80%)`,
        }}>{initials}</div>
    );
};

const ProductDetailsOne = () => {
    const { state } = useLocation();
    const navigate  = useNavigate();
    const product   = state?.product;

    const [quantity,          setQuantity]          = useState(1);
    const [wishlist,          setWishlist]          = useState(false);
    const [toast,             setToast]             = useState({ show: false, message: '', type: '' });
    const [variants,          setVariants]          = useState([]);
    const [selectedVariant,   setSelectedVariant]   = useState(null);
    const [variantsLoading,   setVariantsLoading]   = useState(true);
    const [dbReviews,         setDbReviews]         = useState([]);
    const [reviewForm,        setReviewForm]        = useState({ name: '', rating: 5, comment: '', image: null, imagePreview: null });
    const [reviewError,       setReviewError]       = useState('');
    const [reviewSuccess,     setReviewSuccess]     = useState(false);
    const [submitting,        setSubmitting]        = useState(false);
    const [activeTab,         setActiveTab]         = useState('list');
    const [lightbox,          setLightbox]          = useState(null);
    const [canReview,         setCanReview]         = useState(null);
    const [canReviewReason,   setCanReviewReason]   = useState('');

    const [cartVersion, setCartVersion] = useState(0);
    useEffect(() => {
        const onStorage = () => setCartVersion(v => v + 1);
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const fileInputRef = useRef(null);

    const getCustomerId = () => {
        try { return JSON.parse(localStorage.getItem('customer'))?.id || null; } catch { return null; }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    /* ── Load variants ── */
    useEffect(() => {
        if (!product?.id) return;
        (async () => {
            try {
                setVariantsLoading(true);
                const res  = await getVariantsByProduct(product.id);
                const data = res.data || [];
                setVariants(data);
                setSelectedVariant(data.length > 0 ? data[0] : {
                    id: 'default', variantName: 'Default', unit: '',
                    mrp:        parseFloat(product.mrp || product.oldPrice) || 0,
                    salesPrice: parseFloat(product.salesPrice || product.price) || 0,
                    stock:      999,
                });
            } catch {
                setSelectedVariant({
                    id: 'default', variantName: 'Default', unit: '',
                    mrp:        parseFloat(product.mrp || product.oldPrice) || 0,
                    salesPrice: parseFloat(product.salesPrice || product.price) || 0,
                    stock:      999,
                });
            } finally { setVariantsLoading(false); }
        })();
    }, [product?.id]);

    useEffect(() => { setQuantity(1); }, [selectedVariant?.id]);

    const fetchReviews = async () => {
        try {
            const res  = await fetch(`${API_BASE_URL}/reviews?productId=${product.id}`);
            const data = await res.json();
            setDbReviews(Array.isArray(data) ? data.filter(r => r.status === 'Approved') : []);
        } catch { setDbReviews([]); }
    };
    useEffect(() => { if (product?.id) fetchReviews(); }, [product?.id]);

    useEffect(() => {
        const customerId = getCustomerId();
        if (!product?.id) return;
        if (!customerId) {
            setCanReview(false);
            setCanReviewReason('not_logged_in');
            return;
        }
        fetch(`${API_BASE_URL}/reviews/can-review?customerId=${customerId}&productId=${product.id}`)
            .then(r => r.json())
            .then(data => {
                setCanReview(data.canReview);
                setCanReviewReason(data.reason || '');
            })
            .catch(() => {
                setCanReview(false);
                setCanReviewReason('error');
            });
    }, [product?.id]);

    useEffect(() => {
        try {
            const wl = JSON.parse(localStorage.getItem('wishlist')) || [];
            setWishlist(wl.some(i => i.id === product?.id));
        } catch {}
    }, [product?.id]);

    if (!product) return (
        <section style={{ padding: '80px 0', textAlign: 'center' }}>
            <h4 style={{ fontFamily: "'Sora', sans-serif" }}>Product not found.</h4>
            <button onClick={() => navigate('/')}
                style={{ marginTop: 20, padding: '10px 32px', borderRadius: 99, background: '#F59E0B', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                Go Home
            </button>
        </section>
    );

    const productName  = product.productName || product.name || product.title || 'Product';
    const productImg   = product.img || (product.image ? `${API_BASE_URL}/${product.image}` : '/placeholder-img.png');
    const priceNum     = parseFloat(selectedVariant?.salesPrice || product.salesPrice || product.price) || 0;
    const oldPriceNum  = parseFloat(selectedVariant?.mrp        || product.mrp        || product.oldPrice) || 0;
    const discount     = oldPriceNum > priceNum ? Math.round(((oldPriceNum - priceNum) / oldPriceNum) * 100) : 0;

    const stockQty     = parseInt(selectedVariant?.stock ?? 999);
    const isOutOfStock = stockQty === 0;
    const isLowStock   = stockQty > 0 && stockQty <= 5;

    const cartedQty      = selectedVariant ? getCartedQty(product.id, selectedVariant.id) : 0;
    const remainingStock = Math.max(0, stockQty - cartedQty);
    const isFullyCarted  = !isOutOfStock && remainingStock === 0;
    const addDisabled    = isOutOfStock || isFullyCarted;

    const getVariantLabel = v => {
        if (!v) return 'Default';
        const name = (v.variantName || '').trim();
        const unit = (v.unit || '').trim();
        if (SIZE_UNITS.includes(unit.toUpperCase())) return unit.toUpperCase();
        if (name && unit) return `${name} ${unit}`;
        return name || unit || 'Default';
    };
    const variantLabel = getVariantLabel(selectedVariant);

    const handleQtyChange = type => setQuantity(p => {
        if (type === 'inc') {
            if (p >= remainingStock) return p;
            return p + 1;
        }
        return Math.max(1, p - 1);
    });

    /* ── Add to cart / Buy now ── */
    const addToCart = async (andCheckout = false) => {
        const customerId = getCustomerId();

        // ── Guest check ──────────────────────────────────────────────────────
        if (!customerId) {
            showToast('Please login to continue!', 'warning');
            setTimeout(() => navigate('/account'), 1500);
            return;
        }

        if (addDisabled) return;

        const cartId       = makeCartId(product.id, selectedVariant?.id);
        const existingCart = JSON.parse(localStorage.getItem('cart')) || [];
        const idx          = existingCart.findIndex(i => i.cartId === cartId);

        const currentCarted = idx !== -1 ? existingCart[idx].quantity : 0;
        if (currentCarted >= stockQty) {
            showToast(`Only ${stockQty} in stock — you already have ${currentCarted} in your cart!`, 'warning');
            return;
        }

        const safeQty = Math.min(quantity, stockQty - currentCarted);

        const entry = {
            cartId,
            id:          product.id,
            variantId:   normalizeVariantId(selectedVariant?.id),
            name:        productName,
            variantName: selectedVariant?.variantName || '',
            unit:        selectedVariant?.unit || '',
            weight:      variantLabel,
            price:       priceNum,
            img:         productImg,
            quantity:    safeQty,
            stock:       stockQty,
        };

        if (idx !== -1) {
            const newQty = Math.min(existingCart[idx].quantity + safeQty, stockQty);
            existingCart[idx].quantity = newQty;
            existingCart[idx].stock    = stockQty;
            if (!andCheckout) showToast(`"${productName}" quantity updated!`, 'info');
        } else {
            existingCart.push(entry);
            if (!andCheckout) showToast(`"${productName}" added to cart!`, 'success');
        }

        localStorage.setItem('cart', JSON.stringify(existingCart));
        window.dispatchEvent(new Event('storage'));

        try {
            const res = await fetch(`${API_BASE_URL}/cart/add`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerId,
                    productId:   product.id,
                    variantId:   normalizeVariantId(selectedVariant?.id) !== 'default'
                                     ? selectedVariant?.id
                                     : null,
                    variantName: selectedVariant?.variantName || null,
                    name:        productName,
                    img:         productImg,
                    price:       priceNum,
                    quantity:    safeQty,
                    weight:      variantLabel,
                }),
            });
            if (!res.ok) {
                const errText = await res.text();
                console.error('Cart API failed:', res.status, errText);
            } else {
                window.dispatchEvent(new Event('cartUpdated'));
            }
        } catch (err) {
            console.error('Cart API network error (item saved locally):', err.message);
        }

        if (andCheckout) navigate('/checkout');
    };

    /* ── Wishlist toggle ── */
    const handleToggleWishlist = async () => {
        const customerId = getCustomerId();

        // ── Guest check ──────────────────────────────────────────────────────
        if (!customerId) {
            showToast('Please login to save items to your wishlist!', 'warning');
            setTimeout(() => navigate('/account'), 1500);
            return;
        }

        const wl      = JSON.parse(localStorage.getItem('wishlist')) || [];
        const already = wl.find(i => i.id === product.id);
        let updated;
        if (already) {
            updated = wl.filter(i => i.id !== product.id);
            setWishlist(false);
            showToast(`"${productName}" removed from favourites.`, 'info');
            try {
                const d    = await (await fetch(`${API_BASE_URL}/wishlist/${customerId}`)).json();
                const item = d.find(i => i.productId === product.id);
                if (item) { await fetch(`${API_BASE_URL}/wishlist/${item.id}`, { method: 'DELETE' }); window.dispatchEvent(new Event('cartUpdated')); }
            } catch {}
        } else {
            updated = [...wl, { id: product.id, name: productName, img: productImg, price: priceNum, weight: variantLabel, unit: selectedVariant?.unit || '', category: product.Category?.name || product.category || '', stock: true }];
            setWishlist(true);
            showToast(`"${productName}" added to favourites!`, 'success');
            try {
                await fetch(`${API_BASE_URL}/wishlist/add`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ customerId, productId: product.id, name: productName, img: productImg, price: priceNum, weight: variantLabel, stock: true }),
                });
                window.dispatchEvent(new Event('cartUpdated'));
            } catch {}
        }
        localStorage.setItem('wishlist', JSON.stringify(updated));
        window.dispatchEvent(new Event('storage'));
    };

    /* ── Review image ── */
    const handleImageSelect = e => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setReviewError('Please select a valid image file.'); return; }
        if (file.size > 5 * 1024 * 1024)    { setReviewError('Image must be under 5 MB.');          return; }
        const reader = new FileReader();
        reader.onloadend = () => { setReviewForm(p => ({ ...p, image: file, imagePreview: reader.result })); setReviewError(''); };
        reader.readAsDataURL(file);
    };

    /* ── Submit review ── */
    const handleReviewSubmit = async () => {
        setReviewError('');
        const customerId = getCustomerId();
        if (!customerId) {
            setReviewError('Please login to submit a review.');
            return;
        }
        if (canReview === false) {
            const msg =
                canReviewReason === 'not_purchased'    ? 'You can only review products you have purchased and received.' :
                canReviewReason === 'already_reviewed' ? 'You have already reviewed this product.' :
                'You are not eligible to review this product.';
            setReviewError(msg);
            return;
        }
        if (!reviewForm.name.trim())    { setReviewError('Please enter your name.');    return; }
        if (!reviewForm.comment.trim()) { setReviewError('Please write your review.'); return; }

        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/reviews/add`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    guestName:  reviewForm.name.trim(),
                    feedback:   reviewForm.comment.trim(),
                    rating:     Number(reviewForm.rating),
                    productId:  product.id,
                    customerId: customerId,
                    status:     'Pending',
                }),
            });
            if (res.ok) {
                setReviewForm({ name: '', rating: 5, comment: '', image: null, imagePreview: null });
                if (fileInputRef.current) fileInputRef.current.value = '';
                setReviewSuccess(true);
                setActiveTab('list');
                setCanReview(false);
                setCanReviewReason('already_reviewed');
                setTimeout(() => setReviewSuccess(false), 5000);
                fetchReviews();
            } else {
                const err = await res.json();
                setReviewError(err.error || 'Failed to submit review.');
            }
        } catch { setReviewError('Server error. Please try again.'); }
        finally { setSubmitting(false); }
    };

    const avgRating = dbReviews.length > 0
        ? (dbReviews.reduce((s, r) => s + (r.rating || 0), 0) / dbReviews.length).toFixed(1)
        : null;

    const ratingCounts = [5, 4, 3, 2, 1].map(star => {
        const count = dbReviews.filter(r => Math.round(r.rating) === star).length;
        const pct   = dbReviews.length > 0 ? Math.round((count / dbReviews.length) * 100) : 0;
        return { star, count, pct };
    });

    const canReviewBlockMsg =
        canReviewReason === 'not_logged_in'    ? 'Please login to write a review.' :
        canReviewReason === 'not_purchased'    ? 'Only customers who have purchased and received this product can write a review.' :
        canReviewReason === 'already_reviewed' ? 'You have already submitted a review for this product.' :
        canReviewReason === 'error'            ? 'Unable to verify purchase. Please try again later.' :
        'You are not eligible to review this product.';

    const t = {
        sand:   '#FDFAF5', cream:  '#F7F0E6', terr:   '#C2602C',
        amber:  '#F59E0B', dark:   '#1C0F06', mid:    '#5C3A1E',
        muted:  '#9C7B60', border: '#EAD9C8', white:  '#FFFFFF', green: '#15803D',
    };

    const pill = (bg, color, text) => (
        <span style={{ background: bg, color, borderRadius: 99, fontSize: 11, fontWeight: 700, padding: '3px 10px', fontFamily: "'Sora', sans-serif", letterSpacing: '.03em' }}>{text}</span>
    );

    return (
        <section style={{ minHeight: '100vh', fontFamily: "'Sora', sans-serif" }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,500&family=Sora:wght@400;500;600;700&display=swap');
                * { box-sizing: border-box; margin: 0; padding: 0; }
                @keyframes slideUp   { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:translateY(0) } }
                @keyframes fadeIn    { from { opacity:0 } to { opacity:1 } }
                @keyframes toastIn   { from { opacity:0; transform:translateX(60px) } to { opacity:1; transform:translateX(0) } }
                @keyframes barGrow   { from { width:0 } to { width: var(--w) } }
                @keyframes progress  { from { width:100% } to { width:0 } }
                @keyframes spin      { to { transform:rotate(360deg) } }
                @keyframes pulse     { 0%,100%{opacity:1} 50%{opacity:.6} }

                .pd-section { padding: 48px 20px 80px; max-width: 1180px; margin: 0 auto; animation: slideUp .5s ease; }
                .pd-crumb { display:flex; align-items:center; gap:8px; margin-bottom:32px; font-size:12px; color:${t.muted}; }
                .pd-crumb a { color:${t.muted}; text-decoration:none; transition:color .2s; }
                .pd-crumb a:hover { color:${t.terr}; }
                .pd-crumb-sep { color:${t.border}; }
                .pd-grid { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:start; }
                @media(max-width:760px){ .pd-grid { grid-template-columns:1fr; gap:28px; } }
                .pd-img-wrap {
                    position:relative; border-radius:24px;
                    border:1.5px solid ${t.border}; aspect-ratio:1/1;
                    display:flex; align-items:center; justify-content:center; overflow:hidden;
                }
                .pd-img-wrap img { max-width:78%; max-height:78%; object-fit:contain; transition:transform .4s ease; }
                .pd-badge-off {
                    position:absolute; top:16px; left:16px; background:${t.terr}; color:#fff;
                    font-size:11px; font-weight:700; padding:4px 12px; border-radius:99px; letter-spacing:.04em;
                }
                .pd-wish-btn {
                    position:absolute; top:16px; right:16px;
                    width:40px; height:40px; border-radius:50%; border:1.5px solid ${t.border};
                    background:#fff; display:flex; align-items:center; justify-content:center;
                    cursor:pointer; transition:all .2s; box-shadow:0 2px 8px rgba(0,0,0,.06);
                }
                .pd-wish-btn:hover { border-color:${t.terr}; background:#FFF4EC; transform:scale(1.1); }
                .pd-wish-btn.active { background:#FFF4EC; border-color:${t.terr}; }
                .pd-brand { display:flex; align-items:center; gap:6px; color:${t.muted}; font-size:12px; font-weight:600; margin-bottom:10px; }
                .pd-title { font-family:'Playfair Display',serif; font-size:28px; color:${t.dark}; line-height:1.25; margin-bottom:14px; }
                .pd-rating-row { display:flex; align-items:center; gap:10px; margin-bottom:20px; flex-wrap:wrap; }
                .pd-price-row { display:flex; align-items:baseline; gap:14px; padding-bottom:20px; margin-bottom:20px; border-bottom:1.5px dashed ${t.border}; }
                .pd-price { font-size:34px; font-weight:700; color:${t.dark}; letter-spacing:-.5px; }
                .pd-mrp { font-size:18px; color:${t.muted}; text-decoration:line-through; }
                .pd-save { font-size:12px; font-weight:700; color:#A16207; border:1px solid #FDE68A; border-radius:99px; padding:3px 12px; }
                .pd-variant-label { font-size:12px; font-weight:700; color:${t.mid}; text-transform:uppercase; letter-spacing:.06em; margin-bottom:10px; display:block; }
                .pd-variants { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:24px; }
                .pd-variant-btn {
                    padding:8px 18px; border-radius:10px; font-size:13px; font-weight:600;
                    border:1.5px solid ${t.border}; background:#fff; color:${t.mid};
                    cursor:pointer; transition:all .2s;
                }
                .pd-variant-btn:hover { border-color:${t.terr}; color:${t.terr}; }
                .pd-variant-btn.sel { border-color:${t.terr}; background:#FFF4EC; color:${t.terr}; box-shadow:0 2px 10px rgba(194,96,44,.15); }
                .pd-variant-btn:disabled { opacity:0.4; cursor:not-allowed; border-color:${t.border}; color:${t.muted}; background:#f5f5f5; }
                .pd-actions { display:flex; align-items:center; gap:12px; flex-wrap:wrap; margin-bottom:16px; }
                .pd-qty { display:flex; align-items:center; border:1.5px solid ${t.border}; border-radius:12px; overflow:hidden; background:#fff; }
                .pd-qty-btn { width:38px; height:38px; border:none; background:transparent; font-size:18px; color:${t.terr}; cursor:pointer; transition:background .15s; }
                .pd-qty-btn:hover:not(:disabled) { background:${t.cream}; }
                .pd-qty-btn:disabled { color:${t.muted}; cursor:not-allowed; opacity:0.4; }
                .pd-qty-num { min-width:40px; text-align:center; font-weight:700; font-size:15px; color:${t.dark}; }
                .pd-btn-cart {
                    display:flex; align-items:center; gap:8px; padding:10px 22px;
                    border-radius:12px; border:none; background:${t.terr}; color:#fff;
                    font-size:13px; font-weight:700; cursor:pointer; transition:all .2s;
                    box-shadow:0 4px 16px rgba(194,96,44,.25);
                }
                .pd-btn-cart:hover:not(:disabled) { background:#A3491F; transform:translateY(-2px); box-shadow:0 6px 20px rgba(194,96,44,.3); }
                .pd-btn-cart:disabled { opacity:0.5; cursor:not-allowed; transform:none; box-shadow:none; }
                .pd-btn-buy {
                    display:flex; align-items:center; gap:8px; padding:10px 22px;
                    border-radius:12px; background:green; color:#fff; border:none;
                    font-size:13px; font-weight:700; cursor:pointer; transition:all .2s;
                }
                .pd-btn-buy:hover:not(:disabled) { background:#0C3D7A; transform:translateY(-2px); }
                .pd-btn-buy:disabled { opacity:0.5; cursor:not-allowed; transform:none; }
                .pd-delivery { border:1.5px solid ${t.border}; border-radius:14px; padding:14px 18px; display:flex; flex-direction:column; gap:10px; }
                .pd-delivery-row { display:flex; align-items:center; gap:10px; font-size:13px; color:${t.mid}; }
                .pd-low-stock {
                    display:flex; align-items:center; gap:8px;
                    background:#FFF7ED; border:1px solid #FED7AA;
                    border-radius:10px; padding:10px 14px; margin-bottom:16px;
                    font-size:13px; color:#C2410C; font-weight:600;
                    animation: pulse 2s ease-in-out infinite;
                }
                .rv-section { margin-top:64px; background:#fff; border-radius:24px; border:1.5px solid ${t.border}; overflow:hidden; box-shadow:0 8px 40px rgba(28,15,6,.06); animation: slideUp .5s .1s both; }
                .rv-tab { padding:7px 18px; border-radius:99px; border:1.5px solid ${t.border}; font-size:12px; font-weight:700; cursor:pointer; transition:all .2s; background:#fff; color:${t.mid}; font-family:'Sora',sans-serif; }
                .rv-tab:hover { border-color:${t.terr}; color:${t.terr}; }
                .rv-tab.active { background:${t.terr}; color:#fff; border-color:${t.terr}; }
                .rv-card { display:grid; grid-template-columns:40px 1fr; gap:14px; padding:20px 0; border-bottom:1px solid ${t.border}; animation:slideUp .3s ease; }
                .rv-card:last-child { border-bottom:none; }
                .rv-card-meta { display:flex; align-items:center; gap:10px; margin-bottom:6px; flex-wrap:wrap; }
                .rv-card-name { font-size:13px; font-weight:700; color:${t.dark}; }
                .rv-card-date { font-size:11px; color:${t.muted}; }
                .rv-card-text { font-size:13px; color:${t.mid}; line-height:1.7; margin-top:4px; }
                .rv-card-img { width:56px; height:56px; border-radius:10px; object-fit:cover; margin-top:10px; border:2px solid ${t.border}; cursor:pointer; transition:transform .2s; }
                .rv-card-img:hover { transform:scale(1.08); }
                .rv-empty { padding:48px 32px; text-align:center; color:${t.muted}; font-size:14px; }
                .rv-empty-icon { font-size:40px; margin-bottom:12px; }
                .rv-success { display:flex; align-items:center; gap:10px; background:#F0FDF4; color:${t.green}; border:1.5px solid #BBF7D0; border-radius:12px; padding:12px 16px; margin:20px 32px 0; font-size:13px; font-weight:600; }
                .rv-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px; }
                @media(max-width:560px){ .rv-form-grid { grid-template-columns:1fr; } }
                .rv-form-lbl { font-size:11px; font-weight:700; color:${t.mid}; text-transform:uppercase; letter-spacing:.06em; display:block; margin-bottom:6px; }
                .rv-input { width:100%; padding:10px 14px; border-radius:10px; border:1.5px solid ${t.border}; background:${t.sand}; font-size:13px; color:${t.dark}; outline:none; transition:border-color .2s; font-family:'Sora',sans-serif; }
                .rv-input:focus { border-color:${t.terr}; background:#fff; }
                .rv-textarea { width:100%; padding:10px 14px; border-radius:10px; border:1.5px solid ${t.border}; background:${t.sand}; font-size:13px; color:${t.dark}; outline:none; transition:border-color .2s; resize:vertical; min-height:90px; font-family:'Sora',sans-serif; }
                .rv-textarea:focus { border-color:${t.terr}; background:#fff; }
                .rv-upload { border:2px dashed ${t.border}; border-radius:12px; padding:16px; text-align:center; cursor:pointer; background:${t.sand}; transition:all .2s; font-size:12px; color:${t.muted}; }
                .rv-upload:hover { border-color:${t.terr}; background:#FFF4EC; }
                .rv-error { background:#FEF2F2; color:#B91C1C; border:1.5px solid #FECACA; border-radius:10px; padding:10px 14px; font-size:12px; font-weight:600; margin-bottom:12px; }
                .rv-blocked { display:flex; align-items:flex-start; gap:10px; background:#FFFBEB; color:#92400E; border:1.5px solid #FDE68A; border-radius:12px; padding:14px 16px; font-size:13px; font-weight:500; line-height:1.5; }
                .rv-submit { display:flex; align-items:center; gap:8px; padding:11px 28px; border-radius:12px; border:none; background:linear-gradient(135deg,${t.terr},#A3491F); color:#fff; font-size:13px; font-weight:700; cursor:pointer; box-shadow:0 4px 16px rgba(194,96,44,.3); transition:opacity .2s, transform .2s; font-family:'Sora',sans-serif; }
                .rv-submit:hover:not(:disabled) { opacity:.92; transform:translateY(-2px); }
                .rv-submit:disabled { opacity:.55; cursor:not-allowed; }
                .rv-spinner { width:16px; height:16px; border:2.5px solid rgba(255,255,255,.3); border-top-color:#fff; border-radius:50%; animation:spin .6s linear infinite; }
                .rv-lightbox { position:fixed; inset:0; z-index:99999; background:rgba(0,0,0,.88); display:flex; align-items:center; justify-content:center; animation:fadeIn .2s; }
                .rv-lightbox img { max-width:90vw; max-height:88vh; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.5); }
                .rv-lightbox-x { position:absolute; top:20px; right:24px; background:rgba(255,255,255,.15); border:none; color:#fff; border-radius:50%; width:40px; height:40px; font-size:20px; cursor:pointer; display:flex; align-items:center; justify-content:center; backdrop-filter:blur(4px); }
                .pd-toast { position:fixed; bottom:28px; right:28px; z-index:99999; display:flex; align-items:center; gap:14px; min-width:300px; max-width:400px; padding:14px 18px; border-radius:16px; box-shadow:0 8px 40px rgba(0,0,0,.2); animation:toastIn .4s cubic-bezier(.4,0,.2,1); }
                .pd-toast-s { background:linear-gradient(135deg,#14532D,${t.green}); color:#fff; }
                .pd-toast-i { background:linear-gradient(135deg,#1E3A8A,#1D4ED8); color:#fff; }
                .pd-toast-w { background:linear-gradient(135deg,#92400E,#B45309); color:#fff; }
                .pd-toast-prog { position:absolute; bottom:0; left:0; height:3px; background:rgba(255,255,255,.4); border-radius:0 0 16px 16px; animation:progress 3s linear forwards; }
                @media (max-width: 640px) {
                    .rv-section > div:last-child { display:block !important; }
                    .rv-section > div:last-child > div:first-child { border-right:none !important; border-bottom:1.5px solid ${t.border}; position:static !important; padding:24px 20px !important; }
                    .rv-section > div:last-child > div:last-child { padding:20px !important; }
                }
            `}</style>

            {/* ── Lightbox ── */}
            {lightbox && (
                <div className="rv-lightbox" onClick={() => setLightbox(null)}>
                    <img src={lightbox} alt="Review" />
                    <button className="rv-lightbox-x">✕</button>
                </div>
            )}

            {/* ── Toast ── */}
            {toast.show && (
                <div className={`pd-toast ${toast.type === 'success' ? 'pd-toast-s' : toast.type === 'warning' ? 'pd-toast-w' : 'pd-toast-i'}`}>
                    <span style={{ fontSize: 20 }}>
                        {toast.type === 'success' ? '✅' : toast.type === 'warning' ? '🔒' : '🔄'}
                    </span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                            {toast.type === 'success' ? 'Added!' : toast.type === 'warning' ? 'Login Required' : 'Updated!'}
                        </div>
                        <div style={{ fontSize: 12, opacity: .88 }}>{toast.message}</div>
                    </div>
                    <button style={{ background: 'rgba(255,255,255,.15)', border: 'none', color: '#fff', borderRadius: '50%', width: 26, height: 26, cursor: 'pointer', fontSize: 14 }}
                        onClick={() => setToast({ show: false, message: '', type: '' })}>✕</button>
                    <div className="pd-toast-prog" />
                </div>
            )}

            <div className="pd-section">
                {/* ── Breadcrumb ── */}
                <nav className="pd-crumb">
                    <Link to="/">Home</Link>
                    <span className="pd-crumb-sep">/</span>
                    <Link to="/shop">Shop</Link>
                    <span className="pd-crumb-sep">/</span>
                    <span style={{ color: t.dark, fontWeight: 600 }}>{productName}</span>
                </nav>

                {/* ── Main Grid ── */}
                <div className="pd-grid">

                    {/* LEFT: Image */}
                    <div className="pd-img-wrap">
                        {discount > 0 && !addDisabled && <div className="pd-badge-off">–{discount}% OFF</div>}
                        {addDisabled && (
                            <div className="pd-badge-off" style={{ background: '#B91C1C' }}>Out of Stock</div>
                        )}
                        <button
                            className={`pd-wish-btn${wishlist ? ' active' : ''}`}
                            onClick={handleToggleWishlist}
                            title={wishlist ? 'Remove from wishlist' : 'Add to wishlist'}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill={wishlist ? t.terr : 'none'} stroke={wishlist ? t.terr : t.muted} strokeWidth="2">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                        </button>
                        <img src={productImg} alt={productName}
                            onError={e => { e.target.src = '/placeholder-img.png'; }} />
                        {addDisabled && (
                            <div style={{
                                position: 'absolute', inset: 0,
                                background: 'rgba(255,255,255,0.6)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: 24,
                            }}>
                                <span style={{
                                    background: '#B91C1C', color: '#fff',
                                    fontWeight: 700, fontSize: 14, padding: '8px 24px',
                                    borderRadius: 99, letterSpacing: '.04em',
                                    fontFamily: "'Sora', sans-serif",
                                }}>Out of Stock</span>
                            </div>
                        )}
                    </div>

                    {/* RIGHT: Info */}
                    <div>
                        <div className="pd-brand">
                            <svg width="14" height="14" fill={t.terr} viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            Karpagam Homemade
                        </div>
                        <h1 className="pd-title">{productName}</h1>

                        <div className="pd-rating-row">
                            {avgRating ? (
                                <>
                                    <Stars rating={parseFloat(avgRating)} size={15} />
                                    <span style={{ fontSize: 13, fontWeight: 700, color: t.dark }}>{avgRating}</span>
                                    <span style={{ fontSize: 12, color: t.muted }}>({dbReviews.length} verified reviews)</span>
                                </>
                            ) : (
                                <span style={{ fontSize: 12, color: t.muted }}>No reviews yet</span>
                            )}
                            {addDisabled
                                ? pill('#FEF2F2', '#B91C1C', 'Out of Stock')
                                : isLowStock
                                    ? pill('#FFF7ED', '#C2410C', `Only ${remainingStock} left!`)
                                    : pill('#F0FDF4', t.green, 'In Stock')
                            }
                        </div>

                        <div className="pd-price-row">
                            <span className="pd-price">₹{priceNum}</span>
                            {oldPriceNum > priceNum && <span className="pd-mrp">₹{oldPriceNum}</span>}
                            {discount > 0 && <span className="pd-save">Save ₹{(oldPriceNum - priceNum).toFixed(0)}</span>}
                        </div>

                        {/* Variants */}
                        {!variantsLoading && variants.length > 0 && (
                            <div>
                                <span className="pd-variant-label">Select Size / Quantity</span>
                                <div className="pd-variants">
                                    {variants.map(v => {
                                        const vStock    = parseInt(v.stock ?? 999);
                                        const vCarted   = getCartedQty(product.id, v.id);
                                        const vDisabled = vStock === 0 || vCarted >= vStock;
                                        return (
                                            <button key={v.id}
                                                className={`pd-variant-btn${String(selectedVariant?.id) === String(v.id) ? ' sel' : ''}`}
                                                disabled={vDisabled}
                                                onClick={() => !vDisabled && setSelectedVariant(v)}>
                                                {getVariantLabel(v)}
                                                {vDisabled && (
                                                    <span style={{ fontSize: 9, marginLeft: 4, color: '#B91C1C', fontWeight: 700 }}>✕</span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Low stock / fully carted warning */}
                        {isFullyCarted ? (
                            <div className="pd-low-stock" style={{ background: '#FEF2F2', borderColor: '#FECACA', color: '#B91C1C' }}>
                                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                                You've added all {stockQty} available item{stockQty > 1 ? 's' : ''} to your cart.
                            </div>
                        ) : isLowStock ? (
                            <div className="pd-low-stock">
                                <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                                </svg>
                                Hurry! Only {remainingStock} item{remainingStock > 1 ? 's' : ''} left in stock.
                            </div>
                        ) : null}

                        {/* Qty + CTA */}
                        <div className="pd-actions">
                            <div className="pd-qty">
                                <button className="pd-qty-btn" onClick={() => handleQtyChange('dec')} disabled={quantity <= 1 || addDisabled}>−</button>
                                <span className="pd-qty-num">{quantity}</span>
                                <button className="pd-qty-btn" onClick={() => handleQtyChange('inc')} disabled={addDisabled || quantity >= remainingStock}>+</button>
                            </div>
                            <button className="pd-btn-cart" onClick={() => addToCart(false)} disabled={addDisabled}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                                </svg>
                                {addDisabled ? 'Out of Stock' : `Add to Cart — ₹${(priceNum * quantity).toFixed(0)}`}
                            </button>
                            <button className="pd-btn-buy" onClick={() => addToCart(true)} disabled={addDisabled}>
                                <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                                </svg>
                                {addDisabled ? 'Unavailable' : 'Buy Now'}
                            </button>
                        </div>

                        {/* Delivery info */}
                        <div className="pd-delivery">
                            <div className="pd-delivery-row">
                                <svg width="16" height="16" fill="none" stroke={t.green} strokeWidth="2" viewBox="0 0 24 24">
                                    <rect x="1" y="3" width="15" height="13"/>
                                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
                                    <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                                </svg>
                                <span><strong>Free delivery</strong> on orders above ₹299</span>
                            </div>
                            <div className="pd-delivery-row">
                                <svg width="16" height="16" fill="none" stroke="#1D4ED8" strokeWidth="2" viewBox="0 0 24 24">
                                    <polyline points="1 4 1 10 7 10"/>
                                    <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
                                </svg>
                                <span><strong>7-Day Returns</strong> — Freshness guaranteed</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Reviews Section ── */}
            <div className="rv-section" style={{ margin: '0px 60px 50px', maxWidth: 1400, marginLeft: 'auto', marginRight: 'auto' }}>

                {reviewSuccess && (
                    <div className="rv-success">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                            <polyline points="20 6 9 17 4 12"/>
                        </svg>
                        Your review has been submitted and is awaiting approval. Thank you!
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', alignItems: 'start' }}>

                    {/* LEFT: Rating Summary */}
                    <div style={{ padding: '28px 28px 28px 32px', borderRight: `1.5px solid ${t.border}`, position: 'sticky', top: 16, alignSelf: 'start' }}>
                        {avgRating ? (
                            <>
                                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 64, fontWeight: 700, color: t.dark, lineHeight: 1 }}>{avgRating}</div>
                                <div style={{ marginTop: 8 }}><Stars rating={parseFloat(avgRating)} size={18} /></div>
                                <div style={{ fontSize: 12, color: t.muted, marginTop: 6 }}>{dbReviews.length} verified reviews</div>
                            </>
                        ) : (
                            <div style={{ fontSize: 14, color: t.muted, padding: '8px 0' }}>No ratings yet</div>
                        )}

                        <div style={{ borderTop: `1.5px dashed ${t.border}`, margin: '20px 0' }} />

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                            {ratingCounts.map(({ star, pct }) => (
                                <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, color: t.mid, width: 10, textAlign: 'right', flexShrink: 0 }}>{star}</span>
                                    <svg width="11" height="11" viewBox="0 0 20 20" style={{ flexShrink: 0 }}>
                                        <polygon points="10,1 12.9,7 19.5,7.6 14.5,12 16.2,18.5 10,15 3.8,18.5 5.5,12 0.5,7.6 7.1,7" fill={t.amber} />
                                    </svg>
                                    <div style={{ flex: 1, height: 7, background: t.cream, borderRadius: 99, overflow: 'hidden' }}>
                                        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 99, background: `linear-gradient(90deg, ${t.amber}, ${t.terr})` }} />
                                    </div>
                                    <span style={{ fontSize: 11, color: t.muted, width: 30, textAlign: 'right', flexShrink: 0 }}>{pct}%</span>
                                </div>
                            ))}
                        </div>

                        {activeTab === 'list' ? (
                            canReview === true ? (
                                <button onClick={() => setActiveTab('form')} style={{
                                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                    padding: '11px 0', borderRadius: 12, border: 'none',
                                    background: `linear-gradient(135deg, ${t.terr}, #A3491F)`,
                                    color: '#fff', fontSize: 13, fontWeight: 700,
                                    cursor: 'pointer', fontFamily: "'Sora', sans-serif",
                                    boxShadow: `0 4px 16px rgba(194,96,44,.28)`, transition: 'opacity .2s, transform .2s',
                                }}
                                    onMouseEnter={e => { e.currentTarget.style.opacity = '.88'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                                    </svg>
                                    Write a Review
                                </button>
                            ) : canReview === false ? (
                                <div style={{ background: '#FFFBEB', border: `1.5px solid #FDE68A`, borderRadius: 12, padding: '12px 14px', fontSize: 12, color: '#92400E', lineHeight: 1.55, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                    <span style={{ fontSize: 16, flexShrink: 0 }}>{canReviewReason === 'already_reviewed' ? '✅' : '🔒'}</span>
                                    <span>{canReviewBlockMsg}</span>
                                </div>
                            ) : (
                                <div style={{ height: 44, borderRadius: 12, background: t.cream, animation: 'pulse 1.5s infinite' }} />
                            )
                        ) : (
                            <button onClick={() => setActiveTab('list')} style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                                padding: '11px 0', borderRadius: 12, border: `1.5px solid ${t.border}`,
                                background: '#fff', color: t.mid, fontSize: 13, fontWeight: 700,
                                cursor: 'pointer', fontFamily: "'Sora', sans-serif", transition: 'all .2s',
                            }}
                                onMouseEnter={e => { e.currentTarget.style.borderColor = t.terr; e.currentTarget.style.color = t.terr; }}
                                onMouseLeave={e => { e.currentTarget.style.borderColor = t.border; e.currentTarget.style.color = t.mid; }}>
                                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
                                </svg>
                                Back to Reviews
                            </button>
                        )}
                    </div>

                    {/* RIGHT: Reviews or Form */}
                    <div style={{ padding: '28px 32px', minWidth: 0 }}>

                        {activeTab === 'list' && (
                            <div>
                                {dbReviews.length === 0 ? (
                                    <div className="rv-empty">
                                        <div className="rv-empty-icon">💬</div>
                                        <div style={{ fontWeight: 600, color: t.mid, marginBottom: 6 }}>No approved reviews yet</div>
                                        <div style={{ fontSize: 12 }}>Be the first to share your experience!</div>
                                    </div>
                                ) : (
                                    dbReviews.map((r, i) => (
                                        <div className="rv-card" key={r.id || i}>
                                            <Avatar name={r.guestName || r.customerName || 'A'} />
                                            <div>
                                                <div className="rv-card-meta">
                                                    <span className="rv-card-name">{r.guestName || r.customerName || 'Anonymous'}</span>
                                                    <Stars rating={r.rating} size={12} />
                                                    <span className="rv-card-date">
                                                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                                                    </span>
                                                    {pill('#FFF4EC', t.terr, 'Verified Purchase')}
                                                </div>
                                                <p className="rv-card-text">{r.feedback || r.comment}</p>
                                                {r.imageUrl && (
                                                    <img className="rv-card-img"
                                                        src={`${API_BASE_URL}/${r.imageUrl}`}
                                                        alt="Review"
                                                        onClick={() => setLightbox(`${API_BASE_URL}/${r.imageUrl}`)}
                                                        onError={e => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}

                        {activeTab === 'form' && (
                            <div style={{ animation: 'slideUp .3s ease' }}>
                                <p style={{ fontSize: 13, color: t.muted, marginBottom: 20 }}>
                                    Share your honest experience to help others make better decisions.
                                    Reviews are moderated and appear after approval.
                                </p>

                                {canReview === false ? (
                                    <div className="rv-blocked">
                                        <span style={{ fontSize: 20, flexShrink: 0 }}>{canReviewReason === 'already_reviewed' ? '✅' : '🔒'}</span>
                                        <div>
                                            <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                                {canReviewReason === 'already_reviewed' ? 'Already Reviewed' : 'Review Not Available'}
                                            </div>
                                            {canReviewBlockMsg}
                                            {canReviewReason === 'not_logged_in' && (
                                                <button onClick={() => navigate('/account')} style={{
                                                    marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
                                                    padding: '7px 16px', borderRadius: 8, border: 'none',
                                                    background: t.terr, color: '#fff', fontSize: 12, fontWeight: 700,
                                                    cursor: 'pointer', fontFamily: "'Sora', sans-serif",
                                                }}>
                                                    Login to Continue →
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {reviewError && <div className="rv-error">⚠️ {reviewError}</div>}
                                        <div className="rv-form-grid">
                                            <div>
                                                <label className="rv-form-lbl">Your Name *</label>
                                                <input className="rv-input" placeholder="e.g. Priya K."
                                                    value={reviewForm.name}
                                                    onChange={e => setReviewForm(p => ({ ...p, name: e.target.value }))} />
                                            </div>
                                            <div>
                                                <label className="rv-form-lbl">Your Rating *</label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingTop: 8 }}>
                                                    <Stars rating={reviewForm.rating} size={26} interactive
                                                        onRate={r => setReviewForm(p => ({ ...p, rating: r }))} />
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: t.dark, marginLeft: 6 }}>{reviewForm.rating}/5</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: 16 }}>
                                            <label className="rv-form-lbl">Your Review *</label>
                                            <textarea className="rv-textarea"
                                                placeholder="Tell us about the taste, freshness, packaging, delivery..."
                                                value={reviewForm.comment}
                                                onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))} />
                                        </div>
                                        <button className="rv-submit" onClick={handleReviewSubmit} disabled={submitting || canReview !== true}>
                                            {submitting ? (
                                                <><div className="rv-spinner" /> Submitting…</>
                                            ) : (
                                                <>
                                                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                                                    </svg>
                                                    Submit Review
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProductDetailsOne;